<template>
  <lf-modal :title="title" :close="close">
    <template #content>
      <form novalidate class="p-6" @submit.prevent="onSubmit">
        <div class="row-span-2 grid grid-cols-2 gap-x-4">
          <template v-for="field in combinedFields" :key="field.name">
            <component
              :is="field.component"
              v-if="field.component"
              v-bind="omit(field, ['component'])"
              class="tax-return-component"
              immediate
              @deduction-details:updated="deductionDetails = $event"
            >
              <div v-if="field.component === LfCheckbox">
                {{ field.placeholder }}
              </div>
            </component>
            <div
              v-if="field.name === DIVIDER"
              class="col-span-2 h-5 border-t"
            ></div>
          </template>
        </div>
        <hr class="my-4" />
        <div class="flex justify-end">
          <modal-buttons
            class="bg-white"
            :on-cancel="close"
            :disabled="isSubmitting"
            save-is-submit
            no-vertical-padding
          />
        </div>
      </form>
    </template>
  </lf-modal>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";

import { toFormValidator } from "@vee-validate/zod";
import cloneDeep from "lodash/cloneDeep";
import omit from "lodash/omit";
import { useForm } from "vee-validate";
import { z } from "zod/v4";

import { useBusinessTaxReturn } from "@/hooks/taxReturns";

import LfModal from "@/components/ui/Modal.vue";
import ModalButtons from "@/components/ui/ModalButtons.vue";
import LfCheckbox from "@/components/ui/inputs/LfCheckbox.vue";

import type { TaxReturnFieldDetail } from "@/models/taxReturns";
import type { BusinessTaxReturnBlock } from "@/models/workflows";
import type { SubmissionContext } from "vee-validate";

const { data, isNew, close } = defineProps<{
  data?: BusinessTaxReturnBlock;
  combinedFields: TaxReturnFieldDetail[];
  isNew: boolean;
  close: () => void;
}>();
const { t } = useI18n();

const { DIVIDER, updateBusinessTaxInformation } = useBusinessTaxReturn();

const deductionDetails = ref<{ type: string; amount: string }[]>([]);

const initialValues = reactive({
  ...data,
});

const validationSchema = z.object({
  business_tax_return_business_id: z.string(),
  business_tax_return_year: z.string(),
});

const { handleSubmit, isSubmitting } = useForm({
  initialValues,
  validationSchema: toFormValidator(validationSchema),
});

const title = computed(() => {
  return data?.business_tax_return_information_id
    ? `${t("COMMON.EDIT")} ${data?.business_tax_return_year ?? ""} ${t("DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.BUSINESS_TAX_RETURN")}`
    : `${t("COMMON.ADD")} ${t("DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.BUSINESS_TAX_RETURN")}`;
});

const onSubmit = handleSubmit(
  async (
    values: BusinessTaxReturnBlock,
    actions: SubmissionContext<BusinessTaxReturnBlock>
  ) => {
    const valuesCopy = cloneDeep(values);
    valuesCopy.business_tax_return_other_deductions_detail =
      deductionDetails.value;

    if (!isNew) {
      valuesCopy.business_tax_return_information_id =
        data?.business_tax_return_information_id;
    }

    await updateBusinessTaxInformation(valuesCopy, actions);
    close();
  }
);
</script>

<style scoped>
@reference "@/assets/tailwind.css";

:deep() {
  .tax-return-component.justify-start {
    @apply h-10 items-center;
  }
}
</style>
